import React, { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { currentLanguage } from '../../../i18n/current'
import { exampleTranslationText } from '../../../i18n/localizedContent'
import { AppUseSelector } from '../../../redux/hooks'
import OneLineContent from 'containers/RenderStroyComponent/OneLineContent'
import { wordSimplified } from 'utils/helperFunction'
import { getWordDetail, loadWordExamples } from '../../../redux/actions/storyAction'

interface IWordExamplesProps {
  openExampleModal?: boolean
  setOpenExampleModal: any
  setWordDetailVisible: any
}

const WordExamplesModal = React.forwardRef<HTMLDivElement, IWordExamplesProps>(
  ({ openExampleModal, setOpenExampleModal, setWordDetailVisible }: any, ref: any) => {
    const { t } = useTranslation('reader')
    const { wordDetails, currentStory } = AppUseSelector(({ story }) => story)
    // Example translations (`tr`) come in the request language, the interface language.
    const exampleLanguage = wordDetails?.meaningLanguage || currentLanguage()
    const { memberId } = AppUseSelector(({ user }) => user.credentials)
    const [isLoadingExamples, setIsLoadingExamples] = useState(false)
    const [examples, setExamples] = useState<any[]>([])

    const basicWordInfo = {
      word: wordDetails?.word,
      pinyin: wordDetails?.pinyin,
      english: wordDetails?.english,
    }

    // Load examples when modal opens
    useEffect(() => {
      if (openExampleModal && wordDetails?.word) {
        // Check if examples are already loaded
        if (wordDetails?.sentence && wordDetails.sentence.length > 0) {
          setExamples(wordDetails.sentence)
          setIsLoadingExamples(false)
        } else {
          // Load examples on-demand
          setIsLoadingExamples(true)
          setExamples([])

          const wordData = {
            word: wordDetails.word,
            pinyin: wordDetails.pinyin,
            english: wordDetails.english,
            sentenceString: wordDetails.sentenceString,
            storyId: currentStory?.id,
            level: localStorage.getItem('level'),
          }

          loadWordExamples(wordData)
            .then((loadedExamples) => {
              setExamples(loadedExamples)
              setIsLoadingExamples(false)
            })
            .catch(() => {
              setIsLoadingExamples(false)
            })
        }
      }
    }, [openExampleModal, wordDetails?.word])

    const findWordType = (key) => {
      switch (key) {
        case 'New':
          return {
            ...currentStory,
            newWordsArray: currentStory?.newWordsArray?.length
              ? [...currentStory?.newWordsArray, basicWordInfo]
              : [basicWordInfo],
          }

        case 'Learning':
          return {
            ...currentStory,
            learningWords: currentStory?.learningWords?.length
              ? [...currentStory?.learningWords, basicWordInfo]
              : [basicWordInfo],
          }

        case 'Growing':
          return {
            ...currentStory,
            growingWords: currentStory?.growingWords?.length
              ? [...currentStory?.growingWords, basicWordInfo]
              : [basicWordInfo],
          }

        case 'Developing':
          return {
            ...currentStory,
            developingWords: currentStory?.developingWords?.length
              ? [...currentStory?.developingWords, basicWordInfo]
              : [basicWordInfo],
          }

        case 'Mastered':
          return {
            ...currentStory,
            masteredWords: currentStory?.masteredWords?.length
              ? [...currentStory?.masteredWords, basicWordInfo]
              : [basicWordInfo],
          }
        default:
          return currentStory
      }
    }

    const finalStroyDetail = findWordType(wordDetails?.type)

    return (
      <div className='custom_modal'>
        <Modal
          show={openExampleModal}
          onHide={() => {
            setOpenExampleModal(false)
          }}
          size='lg'
          aria-labelledby='contained-modal-title-vcenter'
          centered
          className='common_modal_main_div'
        >
          <Modal.Body className='mobile_align_div'>
            <div ref={ref}>
              <div className='modal_title_div'>
                <div
                  className='modal_close_icon_div'
                  onClick={() => {
                    setOpenExampleModal(false)
                    // setWordDetailVisible(true)
                  }}
                >
                  <img className='' alt={t('examples.closeAlt')} src={'/images/close-icon-black.svg'} />
                </div>
              </div>

              <div className='sentence_list_main_div'>
                {isLoadingExamples ? (
                  // Skeleton loading animation matching stories page style
                  <>
                    {[1, 2, 3].map((item) => (
                      <div key={item} className='sentence_box_div' style={{ marginBottom: '24px' }}>
                        {/* English sentence skeleton */}
                        <div
                          className='skeleton_animation'
                          style={{ height: '20px', width: '100%', marginBottom: '12px' }}
                        ></div>
                        <div
                          className='skeleton_animation'
                          style={{ height: '20px', width: '75%', marginBottom: '16px' }}
                        ></div>

                        {/* Chinese sentence skeleton */}
                        <div className='view_sentence_word_div'>
                          <div className='word_wrapper' style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {[1, 2, 3, 4, 5, 6, 7, 8].map((wordItem) => (
                              <div
                                key={wordItem}
                                className='skeleton_animation'
                                style={{
                                  height: '32px',
                                  width: `${Math.random() * 40 + 30}px`, // Random width between 30-70px
                                  marginBottom: '8px',
                                }}
                              ></div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                ) : examples?.length > 0 ? (
                  examples?.map((sentenceInfo, i) => {
                    const translation = exampleTranslationText(sentenceInfo, exampleLanguage, sentenceInfo?.english)
                    return (
                      <div key={i} className='sentence_box_div'>
                        <p className='sentence_english' lang={translation.lang}>
                          {translation.text}
                        </p>
                        <div className='view_sentence_word_div'>
                          <div className='word_wrapper'>
                            {sentenceInfo?.segmentation?.map((wordInfo, index) => (
                              <OneLineContent
                                key={index}
                                iterator={`WORD-EXAMPLES-${i}`}
                                wordObj={wordInfo}
                                WIndex={index}
                                contentDetail={sentenceInfo?.segmentation || []}
                                story={finalStroyDetail || currentStory}
                                showPinYin={true}
                                activeIndex={'-1'}
                                handleClickWord={(event, wordObj, sentence) => {
                                  // Close the example modal and open the word detail modal
                                  if (
                                    typeof wordObj !== 'string' &&
                                    !/^[&.\-""，、；？@!#$%^&*()]+$/.test(wordObj?.word) &&
                                    wordObj?.word.trim() !== '。'
                                  ) {
                                    const word = wordObj.word
                                    const sentenceString = sentence.map((w) => w.word).join('')
                                    const basicDetail = {
                                      word,
                                      pinyin: wordObj?.pinyin,
                                      english: wordObj?.english,
                                      sentenceString,
                                      sentenceWords: sentence.map((w) => w.word),
                                    }

                                    // Close example modal and show word detail
                                    setOpenExampleModal(false)
                                    setWordDetailVisible(true)

                                    // Get word details
                                    getWordDetail({
                                      detail: basicDetail,
                                      payload: { word: wordSimplified(word), memberId },
                                      wordType: 'word',
                                    })
                                  }
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <p className='server_error_msg'>{t('examples.noData')}</p>
                )}
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    )
  },
)

export default WordExamplesModal
